import { vn } from 'vend-number'
import { useT } from '@transifex/react'
import { PERMISSIONS } from '../../../../constants'
import {
  getOutstandingBalance,
  isReturn,
  isValidCustomer,
} from '../../../../functions/sale'
import {
  useRetailerSettingsManager,
  useUserManager,
  useWebRegisterStatusManager,
} from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import { selectCurrentSaleAsSale } from '../../../../state/slices/currentSale/currentSaleSlice'
import { SaleStatus } from '../../../../models/sale.constants'
import { containsGiftCard } from '../../../../functions/lineItem'
import { CustomerPaymentOptionsProps } from '../CustomerPaymentOptions'

export type CustomerPaymentOptionsHookProps = Pick<
  CustomerPaymentOptionsProps,
  'systemPaymentTypes' | 'pay' | 'laybyOnAccountSale'
>
/**
 * Build the customer payment buttons:
 * layby, onaccount, loyalty and store credits
 */
export const useCustomerPaymentOptions = (
  props: CustomerPaymentOptionsHookProps
) => {
  const { systemPaymentTypes, pay, laybyOnAccountSale } = props
  const t = useT()
  const userManager = useUserManager()
  const retailerSettingsManager = useRetailerSettingsManager()
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const currentSale = useAppSelector(selectCurrentSaleAsSale)
  const balance = getOutstandingBalance(currentSale)
  const hasNegativeBalance = vn(balance).isNeg()
  const hasValidCustomer = isValidCustomer(currentSale.customer)
  const userCanIssueStoreCredit = userManager.hasPermission(
    PERMISSIONS.STORE_CREDIT_ISSUE_RETURN
  )
  const notReturnSaleAndPositiveBalance =
    !isReturn(currentSale) && !hasNegativeBalance

  const loyaltyPaymentType = systemPaymentTypes.loyaltyPaymentType
  const paymentOptions = []

  // loyalty
  let loyaltyButtonConfig
  if (loyaltyPaymentType && notReturnSaleAndPositiveBalance) {
    loyaltyButtonConfig = {
      id: 'loyalty',
      label: t('Loyalty', {
        _context: 'Loyalty payment type button label',
      }),
      action: () => pay(loyaltyPaymentType),
      isDisabled: false,
    }
    paymentOptions.push(loyaltyButtonConfig)
  }

  // layby payment
  const hasXeroEnabled = retailerSettingsManager.isXeroIntegrationEnabled()
  if (
    notReturnSaleAndPositiveBalance ||
    (hasXeroEnabled && isReturn(currentSale) && !hasNegativeBalance)
  ) {
    if (
      currentSale.displayStatus !== SaleStatus.ACCOUNT_SALE &&
      userManager.hasPermission(PERMISSIONS.SALE_LAYBY) &&
      !containsGiftCard(currentSale.lineItems)
    ) {
      const laybyButtonConfig = {
        id: SaleStatus.LAYBY_SALE,
        label: t('Layaway', {
          _context: 'Layaway payment type button label',
        }),
        action: () => laybyOnAccountSale(SaleStatus.LAYBY_SALE),
        isDisabled: false,
      }
      paymentOptions.push(laybyButtonConfig)
    }
  }

  // store credit payment
  const isStoreCreditEnabled = retailerSettingsManager.isStoreCreditEnabled()

  const storeCreditPaymentType = systemPaymentTypes.storeCreditPaymentType
  if (
    isStoreCreditEnabled &&
    storeCreditPaymentType &&
    (!hasNegativeBalance || userCanIssueStoreCredit)
  ) {
    const storeCreditButtonConfig = {
      id: 'storeCredit',
      label: t('Store credit', {
        _context: 'Store Credit payment type button label',
      }),
      action: () => pay(storeCreditPaymentType),
      isDisabled: false,
    }
    paymentOptions.push(storeCreditButtonConfig)
  }

  // on account payment
  if (notReturnSaleAndPositiveBalance || hasXeroEnabled) {
    if (
      currentSale.displayStatus !== SaleStatus.LAYBY_SALE &&
      userManager.hasPermission(PERMISSIONS.SALE_ON_ACCOUNT)
    ) {
      const onAccountButtonConfig = {
        id: SaleStatus.ACCOUNT_SALE,
        label: t('On account', {
          _context: 'On Account payment type button label',
        }),
        action: () => laybyOnAccountSale(SaleStatus.ACCOUNT_SALE),
        isDisabled: false,
      }
      paymentOptions.push(onAccountButtonConfig)
    }
  }

  // offline and unable to process this sale with loyalty and store credit
  const isOffline = !webRegisterStatusManager.isOnline()
  const isStoreCreditAndLoyaltyPaymentUnable =
    isOffline &&
    (isStoreCreditEnabled || loyaltyButtonConfig) &&
    hasValidCustomer
  return { paymentOptions, isStoreCreditAndLoyaltyPaymentUnable }
}
